import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { MapPin, CreditCard, Banknote, Bitcoin, AlertTriangle } from 'lucide-react';
import type { Pedido } from './types';
import Money from '@/components/Money';
import Pill, { type PillTone } from '@/components/Pill';
import { formatTicket, tempoRelativo } from '@/lib/format';
import { cn } from '@/lib/cn';

interface Props {
  pedido: Pedido;
  acento: 'amber' | 'moss' | 'sage';
  onClick?: () => void;
  /** Quando true, esse card é o overlay de drag */
  overlay?: boolean;
}

const acentoClass = {
  amber: { accent: 'bg-amber-500', ticket: 'text-amber-500', glow: 'hover:shadow-ring-amber' },
  moss: { accent: 'bg-moss-300', ticket: 'text-moss-300', glow: 'hover:shadow-ring-moss' },
  sage: { accent: 'bg-sage-500', ticket: 'text-sage-300', glow: 'hover:shadow-ring-sage' },
};

function payTone(p: string | null | undefined): { tone: PillTone; label: string; Icon: typeof CreditCard } {
  if (!p) return { tone: 'mute', label: '—', Icon: CreditCard };
  const k = p.toLowerCase();
  if (k.includes('pix')) return { tone: 'moss', label: 'Pix', Icon: CreditCard };
  if (k.includes('dinh')) return { tone: 'amber', label: 'Dinheiro', Icon: Banknote };
  if (k.includes('crypt') || k.includes('btc')) return { tone: 'petal', label: 'Crypto', Icon: Bitcoin };
  if (k.includes('cart') || k.includes('credit') || k.includes('debit'))
    return { tone: 'sage', label: 'Cartão', Icon: CreditCard };
  return { tone: 'mute', label: p, Icon: CreditCard };
}

export function PedidoCard({ pedido, acento, onClick, overlay }: Props) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `pedido-${pedido.id}`,
    data: { pedidoId: pedido.id },
    disabled: overlay,
  });

  const style: React.CSSProperties = overlay
    ? {}
    : {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0 : 1,
      };

  // evita abrir drawer se um drag foi iniciado (mesmo que abortado)
  function handleClick() {
    if (isDragging || overlay) return;
    onClick?.();
  }
  function handleKey(e: React.KeyboardEvent) {
    if (overlay) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  }

  const a = acentoClass[acento];
  const pay = payTone(pedido.pagamento);
  const PayIcon = pay.Icon;

  const itensResumo =
    pedido.itens && pedido.itens.length > 0
      ? pedido.itens
          .map((i) => (i.qtd ? `${i.qtd}× ${i.descricao}` : i.descricao))
          .join('\n')
      : '—';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      onKeyDown={handleKey}
      className={cn(
        'group relative bg-card border border-[var(--border)] rounded-md p-s4 select-none',
        'transition-[transform,box-shadow,background] duration-base ease-soft',
        'hover:bg-card-hover hover:-translate-y-[1px] hover:shadow-3',
        'focus-visible:outline-none focus-visible:-translate-y-[1px]',
        'cursor-grab active:cursor-grabbing',
        a.glow,
        overlay && 'rotate-[-1.5deg] scale-[1.04]',
      )}
      aria-roledescription="pedido"
      tabIndex={0}
    >
      <div
        className="absolute left-0 top-s4 bottom-s4 w-[2px] rounded-r-sm transition-opacity duration-base group-hover:opacity-100 group-focus-visible:opacity-100"
        style={{
          background: `var(--col-${acento === 'amber' ? 'pagamento' : acento === 'moss' ? 'pago' : 'separacao'})`,
          opacity: 0.18,
        }}
        aria-hidden
      />

      <div
        className="absolute inset-0 rounded-md pointer-events-none"
        style={{ boxShadow: overlay
          ? '0 24px 60px rgba(0,0,0,0.65), 0 8px 16px rgba(0,0,0,0.5), inset 0 1px 0 rgba(236,235,227,0.04)'
          : 'inset 0 1px 0 rgba(236,235,227,0.04), 0 4px 12px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.3)' }}
      />

      <div className="relative flex justify-between items-baseline mb-s2">
        <span className={cn('font-mono text-[12px]', a.ticket)}>{formatTicket(pedido.numero_ticket)}</span>
        <span className="font-mono text-[10px] text-fg-3">{tempoRelativo(pedido.criado_em)}</span>
      </div>
      <div className="relative font-body font-semibold text-[14px] text-fg-1 leading-snug mb-s2 tracking-[-0.005em]">
        {pedido.nome_cliente}
      </div>
      <div className="relative font-body text-[12.5px] text-fg-2 leading-snug whitespace-pre-line">
        {itensResumo}
      </div>
      <div className="relative flex gap-s2 items-start mt-s3 font-body text-[11px] text-fg-3 leading-snug">
        <MapPin size={11} strokeWidth={1.6} className="mt-[2px] shrink-0 opacity-70" />
        <span className="line-clamp-2 flex-1">{pedido.endereco_entrega}</span>
        {(pedido.latitude == null || pedido.longitude == null) && (
          <span
            className="inline-flex items-center gap-[3px] text-[10px] font-semibold text-amber-500 shrink-0"
            title="Endereço sem coordenadas — não entrará na rota até ser corrigido"
          >
            <AlertTriangle size={10} strokeWidth={1.8} />
            sem coords
          </span>
        )}
      </div>
      <div className="relative flex justify-between items-center mt-s4 pt-s3 border-t border-[var(--border-subtle)]">
        <Pill tone={pay.tone} dot={false}>
          <PayIcon size={11} strokeWidth={1.8} className="mr-[2px]" />
          {pay.label}
        </Pill>
        <Money valor={pedido.total} size="lg" />
      </div>
    </div>
  );
}

export default PedidoCard;
